/**
 * SelectWrapper — Select / MultipleSearchSelect 를 통합하는 얇은 래퍼.
 * 단일 선택이면 Select, multiple 속성이 있으면 MultipleSearchSelect 로 위임.
 * 기존 인터페이스를 유지하므로 호출 코드 변경 불필요.
 */
import { Select, SelectOptions } from "./Select";
import { MultipleSearchSelect, MultipleSearchSelectOptions } from "./MultipleSearchSelect";

export interface SelectWrapperOptions {
  placeholder?: string;
  searchPlaceholder?: string;
  openPosition?: string;
  /** 'top': 검색창을 드롭다운 상단에 고정 (multiSelect 전용) */
  searchPosition?: "top";
  /** 하위 호환 — 신규 구현에서는 드롭다운이 기본적으로 트리거 너비에 맞춰 렌더되어 불필요 */
  contentWidth?: string;
  /** 하위 호환 — 신규 구현에서는 CSS 포지셔닝으로 대체되어 불필요 */
  contentLocation?: HTMLElement | null;
  addable?: boolean | ((value: string) => string | false);
}

export class SelectWrapper {
  private readonly target: string | HTMLSelectElement;
  private readonly options: SelectWrapperOptions;
  private instance: Select | MultipleSearchSelect | null = null;

  constructor(
    target: string | HTMLSelectElement,
    options: SelectWrapperOptions = {}
  ) {
    this.target = target;
    this.options = options;
  }

  init(): Select | MultipleSearchSelect | null {
    const el =
      typeof this.target === "string"
        ? document.querySelector<HTMLSelectElement>(this.target)
        : this.target;
    if (!el) return null;

    const pos = this.options.openPosition as "auto" | "up" | "down" | undefined;

    if (el.multiple) {
      const opts: MultipleSearchSelectOptions = {
        placeholder: this.options.placeholder,
        searchPlaceholder: this.options.searchPlaceholder,
        addable: this.options.addable,
        openPosition: pos,
        searchPosition: this.options.searchPosition,
      };
      try {
        this.instance = new MultipleSearchSelect(el, opts).init();
      } catch {
        return null;
      }
    } else {
      const opts: SelectOptions = {
        placeholder: this.options.placeholder,
        searchPlaceholder: this.options.searchPlaceholder,
        openPosition: pos,
      };
      try {
        this.instance = new Select(el, opts).init();
      } catch {
        return null;
      }
    }
    return this.instance;
  }

  getInstance(): Select | MultipleSearchSelect | null {
    return this.instance;
  }

  destroy(): void {
    this.instance?.destroy();
    this.instance = null;
  }
}
